import { CartItemsComponent } from '@theme/component-cart-items';
import { morphSection, sectionRenderer } from '@theme/section-renderer';
import { CartSectionRestoredEvent } from '@theme/events';
import { CartLinesUpdateEvent } from '@shopify/events';

export function resolveCartOptions(variants, selected) {
  const available = variants.filter((variant) => variant.available);
  const values = [];
  const choices = [];
  const count = available[0]?.options.length ?? 0;
  for (let index = 0; index < count; index += 1) {
    const matching = available.filter((variant) => values.every((value, position) => variant.options[position] === value));
    choices[index] = [...new Set(matching.map((variant) => variant.options[index]))];
    values[index] = choices[index].includes(selected[index]) ? selected[index] : choices[index][0];
  }
  return {
    choices,
    values,
    variant: available.find((variant) => variant.options.every((value, index) => value === values[index])),
  };
}

function propertiesKey(properties) {
  return JSON.stringify(Object.entries(properties ?? {}).sort(([left], [right]) => left.localeCompare(right)));
}

function matchingLines(cart, variantId, properties) {
  return cart.items.filter((item) => String(item.variant_id) === String(variantId)
    && propertiesKey(item.properties) === propertiesKey(properties)
    && !item.selling_plan_allocation && !item.parent_relationship);
}

function totalQuantity(lines) {
  return lines.reduce((total, item) => total + item.quantity, 0);
}

function editorError(code, cart) {
  const error = new Error(code);
  error.cart = cart;
  return error;
}

export async function replaceCartVariant(request, lineKey, variantId, sections) {
  const before = await request('cart.js');
  const source = before.items.find((item) => item.key === lineKey);
  if (!source) throw editorError('changed', before);
  if (String(source.variant_id) === String(variantId)) return before;
  if (source.selling_plan_allocation || source.parent_relationship || source.item_components?.length
    || source.instructions?.can_remove === false || source.instructions?.can_update_quantity === false
    || before.items.some((item) => item.parent_relationship?.parent_key === source.key || item.parent_relationship?.parent?.key === source.key)) {
    throw editorError('changed', before);
  }
  const originalLines = matchingLines(before, source.variant_id, source.properties);
  const existingTargets = matchingLines(before, variantId, source.properties);
  if (originalLines.length !== 1 || existingTargets.length > 1) throw editorError('changed', before);
  const oldTargetQuantity = totalQuantity(existingTargets);
  const expectedTargetQuantity = oldTargetQuantity + source.quantity;
  let added;
  try {
    await request('cart/add.js', { items: [{ id: Number(variantId), quantity: source.quantity, properties: source.properties ?? {} }] });
  } catch (_) {
    added = false;
  }
  let afterAdd;
  try {
    afterAdd = await request('cart.js');
  } catch (_) {
    throw editorError('review');
  }
  const targets = matchingLines(afterAdd, variantId, source.properties);
  const originals = matchingLines(afterAdd, source.variant_id, source.properties);
  const targetQuantity = totalQuantity(targets);
  const sourceUnchanged = originals.length === 1 && originals[0].quantity === source.quantity;
  const undoAddition = async (cart) => {
    const targetLines = matchingLines(cart, variantId, source.properties);
    const currentQuantity = totalQuantity(targetLines);
    if (currentQuantity === oldTargetQuantity) return cart;
    if (targetLines.length !== 1 || currentQuantity < oldTargetQuantity || currentQuantity > expectedTargetQuantity) {
      throw editorError('review', cart);
    }
    try {
      await request('cart/change.js', { id: targetLines[0].key, quantity: oldTargetQuantity, ...sections });
    } catch (_) {}
    let restored;
    try {
      restored = await request('cart.js');
    } catch (_) {
      throw editorError('review');
    }
    if (totalQuantity(matchingLines(restored, variantId, source.properties)) !== oldTargetQuantity
      || totalQuantity(matchingLines(restored, source.variant_id, source.properties)) !== source.quantity) {
      throw editorError('review', restored);
    }
    return restored;
  };
  if (!sourceUnchanged) throw editorError('review', afterAdd);
  if (targets.length !== 1 || targetQuantity !== expectedTargetQuantity) {
    const restored = await undoAddition(afterAdd);
    throw editorError(added === false ? 'unavailable' : 'failed', restored);
  }
  let finalCart;
  try {
    finalCart = await request('cart/change.js', { id: originals[0].key, quantity: 0, ...sections });
  } catch (_) {
    try {
      finalCart = await request('cart.js');
    } catch (_) {
      throw editorError('review');
    }
  }
  const remainingOriginals = totalQuantity(matchingLines(finalCart, source.variant_id, source.properties));
  const remainingTargets = totalQuantity(matchingLines(finalCart, variantId, source.properties));
  if (remainingOriginals === 0 && remainingTargets === expectedTargetQuantity) return finalCart;
  if (remainingOriginals === source.quantity && remainingTargets === expectedTargetQuantity) {
    const restored = await undoAddition(finalCart);
    throw editorError('failed', restored);
  }
  throw editorError('review', finalCart);
}

class CartVariantEditor extends HTMLElement {
  static busy = false;

  connectedCallback() {
    if (this.ready) return;
    this.ready = true;
    this.labels = JSON.parse(this.querySelector('[data-editor-labels]').textContent);
    this.details = this.querySelector('details');
    this.status = this.querySelector('[data-editor-status]');
    this.optionsContainer = this.querySelector('[data-editor-options]');
    this.actions = this.querySelector('[data-editor-actions]');
    this.saveButton = this.querySelector('[data-editor-save]');
    this.price = this.querySelector('[data-editor-price]');
    this.details.addEventListener('toggle', () => {
      if (this.details.open) this.openEditor();
    });
    this.querySelector('[data-editor-cancel]').addEventListener('click', () => this.cancel());
    this.saveButton.addEventListener('click', () => this.save());
    this.optionsContainer.addEventListener('change', () => this.updateOptions());
    this.addEventListener('keydown', (event) => {
      if (event.key === 'Escape' && this.details.open && !CartVariantEditor.busy) {
        event.preventDefault();
        event.stopPropagation();
        this.cancel();
      }
    });
    this.hidden = false;
  }

  cancel() {
    this.details.open = false;
    this.querySelector('summary').focus();
  }

  async openEditor() {
    if (this.loading) return;
    this.loading = true;
    this.status.textContent = this.labels.loading;
    this.actions.hidden = true;
    this.optionsContainer.replaceChildren();
    this.price.hidden = true;
    this.querySelector('[data-editor-fallback]').hidden = true;
    try {
      const url = new URL(this.dataset.productUrl, window.location.origin);
      url.pathname = url.pathname.replace(/\/$/, '') + '.js';
      url.search = '';
      const response = await fetch(url, { credentials: 'same-origin', cache: 'no-store', headers: { Accept: 'application/json' } });
      if (!response.ok) throw new Error('loadFailed');
      this.product = await response.json();
      if (!Array.isArray(this.product.variants) || this.product.variants.length >= 250) throw new Error('viewOptions');
      const current = this.product.variants.find((variant) => String(variant.id) === this.dataset.currentVariant);
      if (!this.product.variants.some((variant) => variant.available)) throw new Error('noOptions');
      const resolved = resolveCartOptions(this.product.variants, current?.options ?? []);
      this.selects = this.product.options.map((option, index) => {
        const label = document.createElement('label');
        label.className = 'about-cart-editor__field';
        const title = document.createElement('span');
        const name = typeof option === 'string' ? option : option.name;
        title.textContent = /^colou?r$/i.test(name) ? this.labels.colour : name;
        const select = document.createElement('select');
        select.dataset.optionIndex = String(index);
        label.append(title, select);
        this.optionsContainer.append(label);
        return select;
      });
      this.updateOptions(resolved.values);
      this.status.textContent = '';
      this.actions.hidden = false;
    } catch (error) {
      this.status.textContent = this.labels[error.message] ?? this.labels.loadFailed;
      this.querySelector('[data-editor-fallback]').hidden = false;
    } finally {
      this.loading = false;
    }
  }

  updateOptions(values) {
    const selected = Array.isArray(values) ? values : this.selects.map((select) => select.value);
    const resolved = resolveCartOptions(this.product.variants, selected);
    this.selects.forEach((select, index) => {
      select.replaceChildren(...resolved.choices[index].map((value) => {
        const option = document.createElement('option');
        option.value = value;
        option.textContent = value;
        option.selected = value === resolved.values[index];
        return option;
      }));
    });
    this.selectedVariant = resolved.variant;
    this.saveButton.disabled = !resolved.variant || String(resolved.variant.id) === this.dataset.currentVariant;
    if (resolved.variant) {
      const currency = window.Shopify?.currency?.active ?? this.dataset.currency;
      const amount = new Intl.NumberFormat(document.documentElement.lang || 'en-GB', { style: 'currency', currency }).format(resolved.variant.price / 100);
      this.price.textContent = this.labels.price.replace('[price]', amount);
      this.price.hidden = false;
    }
  }

  async request(route, body) {
    const response = await fetch((window.Shopify?.routes?.root ?? '/') + route, {
      method: body ? 'POST' : 'GET',
      credentials: 'same-origin',
      cache: 'no-store',
      headers: { Accept: 'application/json', ...(body ? { 'Content-Type': 'application/json' } : {}) },
      ...(body ? { body: JSON.stringify(body) } : {}),
    });
    const data = await response.json();
    if (!response.ok || data.status >= 400 || data.errors) throw new Error('failed');
    return data;
  }

  async save() {
    if (CartVariantEditor.busy || !this.selectedVariant || this.saveButton.disabled) return;
    const owner = this.closest('cart-items-component');
    if (!owner?.dataset.sectionId) return;
    CartVariantEditor.busy = true;
    const variantId = this.selectedVariant.id;
    const sectionId = owner.dataset.sectionId;
    const mode = owner.dataset.drawer !== undefined ? 'hydration' : 'full';
    const locked = [...document.querySelectorAll('cart-items-component, product-form-component')].map((element) => ({ element, inert: element.inert }));
    for (const { element } of locked) element.inert = true;
    this.setAttribute('aria-busy', 'true');
    this.status.textContent = this.labels.saving;
    let deferred;
    let cart;
    let message;
    let failed = false;
    try {
      await new Promise((resolve) => setTimeout(resolve, 350));
      await Promise.allSettled(CartItemsComponent.pendingQuantityUpdates);
      const sectionIds = [...new Set([...document.querySelectorAll('cart-items-component')].map((element) => element.dataset.sectionId).filter(Boolean))].slice(0, 5);
      for (const id of sectionIds) sectionRenderer.abortRender(id);
      deferred = CartLinesUpdateEvent.createPromise();
      owner.dispatchEvent(new CartLinesUpdateEvent({ action: 'update', context: 'cart', lines: [], promise: deferred.promise }));
      cart = await replaceCartVariant(this.request.bind(this), this.dataset.lineKey, variantId, {
        sections: sectionIds,
        sections_url: window.location.pathname,
      });
      message = this.labels.saved;
    } catch (error) {
      failed = true;
      message = this.labels[error.message] ?? this.labels.failed;
      cart = error.cart;
      if (!cart) {
        try { cart = await this.request('cart.js'); } catch (_) { message = this.labels.review; }
      }
    }
    try {
      if (cart) {
        const html = cart.sections?.[sectionId];
        if (html) {
          await morphSection(sectionId, html, { mode });
        } else {
          await sectionRenderer.renderSection(sectionId, { cache: false, mode });
        }
        deferred?.resolve({
          cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
          detail: { sections: cart.sections, items: cart.items, itemCount: cart.item_count, source: 'cart-variant-editor', didError: failed },
        });
        const updatedOwner = document.querySelector(`cart-items-component[data-section-id="${sectionId}"]`);
        updatedOwner?.dispatchEvent(new CartSectionRestoredEvent());
        for (const selector of document.querySelectorAll('quantity-selector-component[data-variant-id]')) {
          const quantity = cart.items.filter((item) => String(item.variant_id) === selector.dataset.variantId)
            .reduce((total, item) => total + item.quantity, 0);
          selector.querySelector('input[data-cart-quantity]')?.setAttribute('data-cart-quantity', String(quantity));
          selector.updateCartQuantity?.();
        }
        for (const selector of document.querySelectorAll('cart-quantity-selector-component')) selector.updateButtonStates?.();
        this.announce(updatedOwner, message, failed);
      } else {
        deferred?.reject(new Error(message));
        this.status.textContent = message;
      }
    } catch (_) {
      deferred?.resolve({
        cart: cart ? CartLinesUpdateEvent.createCartFromAjaxResponse(cart) : undefined,
        detail: { itemCount: cart?.item_count, didError: true },
      });
      this.announce(document.querySelector(`cart-items-component[data-section-id="${sectionId}"]`), this.labels.refresh, true, variantId);
    } finally {
      for (const { element, inert } of locked) element.inert = inert;
      document.querySelector(`cart-items-component[data-section-id="${sectionId}"]`)?.removeAttribute('aria-busy');
      this.removeAttribute('aria-busy');
      CartVariantEditor.busy = false;
      requestAnimationFrame(() => {
        const currentOwner = document.querySelector(`cart-items-component[data-section-id="${sectionId}"]`);
        const focusTarget = failed ? currentOwner?.querySelector('.about-cart-editor-feedback')
          : currentOwner?.querySelector(`cart-variant-editor[data-current-variant="${variantId}"] summary`);
        focusTarget?.focus({ preventScroll: true });
      });
    }
  }

  announce(owner, message, failed) {
    if (!owner) return;
    let feedback = owner.querySelector('.about-cart-editor-feedback');
    if (!feedback) {
      feedback = document.createElement('p');
      feedback.className = 'about-cart-editor-feedback';
      feedback.tabIndex = -1;
      owner.querySelector('.cart-items__wrapper')?.prepend(feedback);
    }
    feedback.setAttribute('role', failed ? 'alert' : 'status');
    feedback.textContent = message;
  }
}

if (!customElements.get('cart-variant-editor')) customElements.define('cart-variant-editor', CartVariantEditor);
